import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CardActionArea,
  CircularProgress,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ResponsiveBar } from '@nivo/bar';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import Header from 'shared/components/Header';
import { tokens } from 'shared/theme';
import { useAuth } from 'shared/hooks/useAuth';
import { useTenantNavigate } from 'shared/hooks/useTenantNavigate';
import {
  SERVICE_CODES,
  SERVICE_CODE_OPTIONS,
  SERVICE_PATHS,
} from 'shared/constants/serviceCodes';
import {
  profileHasTenantServiceCode,
  profileTenantServiceCodes,
  resolveServiceLabel,
} from 'platform/rbac/Permissions';
import { softwareApi } from 'services/system-register/api/systemRegisterApi';
import { unwrapRecords } from 'services/system-register/utils/responseHelpers';

const SERVICE_CARD_META = {
  [SERVICE_CODES.PAYROLL]: {
    icon: AccountBalanceOutlinedIcon,
    blurb: 'Run payroll, allowances, and employee compensation workflows.',
    accent: '#1B7A6E',
  },
  [SERVICE_CODES.CONTRACT]: {
    icon: DescriptionOutlinedIcon,
    blurb: 'Create, track, and manage organizational contracts.',
    accent: '#C45C26',
  },
  [SERVICE_CODES.SYSTEM_REGISTER]: {
    icon: GavelOutlinedIcon,
    blurb:
      'Register and govern systems, assets, documents, risks, reviews, changes, incidents, integrations, access, and approvals.',
    accent: '#0D4F8B',
  },
};

function daysLeftFrom(endDate) {
  if (endDate == null || endDate === '') return null;
  const raw = String(endDate).trim();
  if (!raw) return null;
  const end = new Date(raw.includes('T') ? raw : `${raw}T00:00:00`);
  if (Number.isNaN(end.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return Math.round((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function chartValue(days) {
  if (days == null) return undefined;
  return Math.max(0, days);
}

function shortLabel(value, max = 14) {
  const text = String(value || '').trim();
  if (!text) return '—';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

const ChartState = ({ children }) => (
  <Box
    height="100%"
    minHeight={220}
    display="flex"
    alignItems="center"
    justifyContent="center"
    px={2}
  >
    {children}
  </Box>
);

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === 'dark';
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { navigateTenant } = useTenantNavigate();
  const { user } = useAuth();
  const profile = user?.data;

  const [systems, setSystems] = useState([]);
  const [loadingSystems, setLoadingSystems] = useState(false);
  const [systemsError, setSystemsError] = useState('');

  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;
  const chartInk = isDark ? colors.grey[100] : colors.grey[800] || '#1f2933';
  const chartMuted = isDark ? alpha(colors.grey[100], 0.78) : colors.grey[600] || '#52606d';

  useEffect(() => {
    const msg = location.state?.loginSuccessMessage;
    if (msg) {
      toast.success(msg, { duration: 5000 });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const subscribedCodes = useMemo(() => {
    const codes = profileTenantServiceCodes(profile);
    if (codes.length) return codes;
    return SERVICE_CODE_OPTIONS.map((o) => o.value).filter((code) =>
      profileHasTenantServiceCode(profile, code)
    );
  }, [profile]);

  const hasItGovernance = profileHasTenantServiceCode(profile, SERVICE_CODES.SYSTEM_REGISTER);

  useEffect(() => {
    if (!hasItGovernance) {
      setSystems([]);
      setSystemsError('');
      return undefined;
    }

    let cancelled = false;
    setLoadingSystems(true);
    setSystemsError('');

    softwareApi
      .list({ page: 0, size: 200, status: 'ACTIVE' })
      .then((response) => {
        if (cancelled) return;
        const { rows } = unwrapRecords(response);
        setSystems(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (cancelled) return;
        setSystems([]);
        setSystemsError('Unable to load systems for the charts.');
      })
      .finally(() => {
        if (!cancelled) setLoadingSystems(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hasItGovernance]);

  const chartData = useMemo(() => {
    return (systems || [])
      .map((row) => {
        const name = String(row.softwareName || '').trim() || `System ${row.id ?? ''}`;
        const contractDays = chartValue(daysLeftFrom(row.contractEndDate));
        const licenseDays = chartValue(daysLeftFrom(row.licenseEndDate));
        if (contractDays == null && licenseDays == null) return null;
        const point = { system: name, systemShort: shortLabel(name, isXs ? 10 : 16) };
        if (contractDays != null) point.Contract = contractDays;
        if (licenseDays != null) point.License = licenseDays;
        return point;
      })
      .filter(Boolean);
  }, [systems, isXs]);

  const openService = (serviceCode) => {
    const path = SERVICE_PATHS[serviceCode];
    if (!path) {
      toast.error('No destination configured for this service.');
      return;
    }
    navigateTenant(path);
  };

  const chartTheme = {
    text: { fill: chartInk, fontSize: isXs ? 11 : 12 },
    axis: {
      domain: { line: { stroke: alpha(chartInk, 0.45) } },
      legend: {
        text: {
          fill: chartInk,
          fontSize: isXs ? 11 : 13,
          fontWeight: 800,
        },
      },
      ticks: {
        line: { stroke: alpha(chartInk, 0.35), strokeWidth: 1 },
        text: {
          fill: chartInk,
          fontSize: isXs ? 10 : 12,
          fontWeight: 600,
        },
      },
    },
    legends: {
      text: {
        fill: chartInk,
        fontSize: isXs ? 11 : 12,
        fontWeight: 700,
      },
    },
    labels: {
      text: {
        fill: '#ffffff',
        fontSize: isXs ? 10 : 11,
        fontWeight: 800,
      },
    },
    tooltip: {
      container: {
        background: isDark ? colors.primary[500] : '#fff',
        color: textPrimary,
        fontSize: 13,
        fontWeight: 600,
      },
    },
    grid: { line: { stroke: alpha(chartInk, isDark ? 0.22 : 0.14) } },
  };

  const daysViewportHeight = isXs ? 260 : 280;
  const daysInnerWidth = Math.max(
    isXs ? 320 : 520,
    chartData.length * (isXs ? 70 : 88)
  );

  const daysMargin = isXs
    ? { top: 12, right: 12, bottom: 78, left: 36 }
    : { top: 16, right: 100, bottom: 72, left: 40 };

  return (
    <Box
      sx={{
        px: { xs: 1.5, sm: 2.5, md: 3 },
        pt: { xs: 1.5, sm: 2 },
        pb: { xs: 8, sm: 10 },
        maxWidth: 1400,
        mx: 'auto',
        width: '100%',
      }}
    >
      <Header title="Datahouse-TZ" subtitle="Your subscribed services and system outlook" />

      {subscribedCodes.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            mt: { xs: 2, sm: 2.5 },
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            bgcolor: isDark ? colors.primary[400] : alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Typography sx={{ color: textSecondary, fontSize: { xs: '0.9rem', sm: '0.95rem' }, lineHeight: 1.5 }}>
            No services are assigned to this tenant yet. Contact an administrator to subscribe.
          </Typography>
        </Paper>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))',
          }}
          gap={{ xs: 1.5, sm: 2 }}
          mt={{ xs: 2, sm: 2.5 }}
        >
          {subscribedCodes.map((code) => {
            const meta = SERVICE_CARD_META[code] || {
              icon: GavelOutlinedIcon,
              blurb: 'Open this subscribed service.',
              accent: theme.palette.secondary.main,
            };
            const Icon = meta.icon;
            const label = resolveServiceLabel(code);
            const accent = meta.accent;

            return (
              <Paper
                key={code}
                elevation={0}
                sx={{
                  borderRadius: 2.5,
                  overflow: 'hidden',
                  border: `1px solid ${alpha(accent, isDark ? 0.5 : 0.28)}`,
                  background: isDark
                    ? `linear-gradient(145deg, ${alpha(accent, 0.34)} 0%, ${colors.primary[400]} 58%)`
                    : `linear-gradient(145deg, ${alpha(accent, 0.14)} 0%, #fff 52%)`,
                  boxShadow: isDark
                    ? `0 10px 28px ${alpha('#000', 0.35)}`
                    : `0 10px 28px ${alpha(accent, 0.12)}`,
                  transition: 'transform 160ms ease, box-shadow 160ms ease',
                  '&:hover': {
                    transform: { sm: 'translateY(-3px)' },
                    boxShadow: isDark
                      ? `0 14px 34px ${alpha('#000', 0.45)}`
                      : `0 14px 34px ${alpha(accent, 0.18)}`,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => openService(code)}
                  sx={{
                    p:
                      code === SERVICE_CODES.SYSTEM_REGISTER
                        ? { xs: 1.25, sm: 1.5 }
                        : { xs: 1.75, sm: 2.25 },
                    height: '100%',
                    alignItems: 'stretch',
                    display: 'flex',
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={code === SERVICE_CODES.SYSTEM_REGISTER ? 0.85 : 1.25}
                    width="100%"
                    minHeight={
                      code === SERVICE_CODES.SYSTEM_REGISTER
                        ? { xs: 96, sm: 108 }
                        : { xs: 120, sm: 140 }
                    }
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                      <Box
                        sx={{
                          width: { xs: 42, sm: 48 },
                          height: { xs: 42, sm: 48 },
                          borderRadius: 1.5,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: alpha(accent, isDark ? 0.4 : 0.18),
                          color: isDark ? '#F4F8FF' : accent,
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: { xs: 22, sm: 26 } }} />
                      </Box>
                      <ArrowForwardRoundedIcon
                        sx={{ color: isDark ? alpha('#fff', 0.9) : accent, fontSize: { xs: 20, sm: 24 } }}
                      />
                    </Box>
                    <Box minWidth={0}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: { xs: '1.15rem', sm: '1.35rem' },
                          lineHeight: 1.25,
                          color: textPrimary,
                          wordBreak: 'break-word',
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography
                        sx={{
                          mt: code === SERVICE_CODES.SYSTEM_REGISTER ? 0.4 : 0.75,
                          fontSize: {
                            xs: code === SERVICE_CODES.SYSTEM_REGISTER ? '0.78rem' : '0.84rem',
                            sm: code === SERVICE_CODES.SYSTEM_REGISTER ? '0.82rem' : '0.9rem',
                          },
                          lineHeight: 1.4,
                          color: textSecondary,
                          fontWeight: 500,
                          display: '-webkit-box',
                          WebkitLineClamp: code === SERVICE_CODES.SYSTEM_REGISTER ? 2 : 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {meta.blurb}
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Paper>
            );
          })}
        </Box>
      )}

      {hasItGovernance ? (
        <Box
          mt={{ xs: 3, sm: 4 }}
          display="grid"
          gridTemplateColumns="1fr"
          gap={{ xs: 2.5, sm: 3 }}
          alignItems="stretch"
        >
          <Box minWidth={0}>
            <Typography
              component="h2"
              sx={{
                mb: 0.5,
                fontWeight: 800,
                fontSize: { xs: '1.05rem', sm: '1.2rem' },
                color: textPrimary,
              }}
            >
              System Contract / License Days Until Expiry
            </Typography>
            <Paper
              elevation={0}
              sx={{
                height: daysViewportHeight,
                borderRadius: 2.5,
                border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.4 : 0.16)}`,
                bgcolor: isDark ? colors.primary[400] : '#fff',
                p: { xs: 1, sm: 1.25 },
                overflow: 'auto',
              }}
            >
              {loadingSystems ? (
                <ChartState>
                  <CircularProgress size={34} />
                </ChartState>
              ) : systemsError ? (
                <ChartState>
                  <Typography sx={{ color: textSecondary, textAlign: 'center', fontWeight: 600 }}>
                    {systemsError}
                  </Typography>
                </ChartState>
              ) : chartData.length === 0 ? (
                <ChartState>
                  <Typography sx={{ color: textSecondary, textAlign: 'center', fontWeight: 600 }}>
                    No systems with contract or license end dates yet.
                  </Typography>
                </ChartState>
              ) : (
                <Box sx={{ width: daysInnerWidth, minWidth: '100%', height: daysViewportHeight - 20 }}>
                  <ResponsiveBar
                    data={chartData}
                    keys={['Contract', 'License']}
                    indexBy="systemShort"
                    margin={daysMargin}
                    padding={isXs ? 0.22 : 0.28}
                    groupMode="grouped"
                    valueScale={{ type: 'linear', min: 0 }}
                    indexScale={{ type: 'band', round: true }}
                    colors={[theme.palette.secondary.main, '#0D4F8B']}
                    borderRadius={3}
                    borderColor={{ from: 'color', modifiers: [['darker', 1.2]] }}
                    theme={chartTheme}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 8,
                      tickRotation: isXs || chartData.length > 3 ? -35 : 0,
                      legend: isXs ? undefined : 'Systems',
                      legendPosition: 'middle',
                      legendOffset: 60,
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 8,
                      tickRotation: 0,
                    }}
                    enableLabel={false}
                    legends={
                      isXs
                        ? [
                            {
                              dataFrom: 'keys',
                              anchor: 'bottom',
                              direction: 'row',
                              translateY: 70,
                              itemWidth: 90,
                              itemHeight: 16,
                              itemsSpacing: 12,
                              symbolSize: 10,
                            },
                          ]
                        : [
                            {
                              dataFrom: 'keys',
                              anchor: 'bottom-right',
                              direction: 'column',
                              translateX: 92,
                              translateY: 0,
                              itemsSpacing: 6,
                              itemWidth: 90,
                              itemHeight: 18,
                              itemDirection: 'left-to-right',
                              symbolSize: 12,
                            },
                          ]
                    }
                    tooltip={({ id, value, indexValue, color, data: row }) => (
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1,
                          borderRadius: 1,
                          bgcolor: isDark ? colors.primary[500] : '#fff',
                          border: `1px solid ${alpha(color, 0.5)}`,
                          boxShadow: 3,
                          maxWidth: 240,
                        }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: textPrimary }}>
                          {row?.system || indexValue}
                        </Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color }}>
                          {id}: {value} day{value === 1 ? '' : 's'} left
                        </Typography>
                      </Box>
                    )}
                    role="application"
                    ariaLabel="Days left for contract and license by system"
                  />
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default Dashboard;
